import {
  BasicComponentProps,
  Layout,
  LayoutComponent,
  Middleware,
  Route,
  RouterGroupOptions,
} from "./types";
import { concatPath, getLocaleCodes } from "./helpers";
import { getCurrentBaseAppPath } from "./apps";
import React from "react";

/**
 * list of layouts that will wrap the application routes
 *
 * @const {Layout[]}
 */
export const layoutsList: Layout[] = [];

/**
 * Fragment element
 *
 */
export const FragmentElement: LayoutComponent = ({ key, children }) => (
  <React.Fragment key={key}>{children}</React.Fragment>
);

/**
 * add new router to the application routes
 *
 * @param {string} path
 * @param {React.Component} component
 * @param {Middleware} middleware
 * @returns {void}
 */
export function addRouter(
  path: string,
  component: React.FunctionComponent<BasicComponentProps>,
  middleware?: Middleware
): void {
  partOfLayout(FragmentElement, [
    {
      path,
      component,
      middleware,
    },
  ]);
}

/**
 * routes will be a part of specific layout
 *
 * @param LayoutComponent
 * @param routes
 * @param middleware
 * @returns {void}
 */
export function partOfLayout(
  LayoutComponent: LayoutComponent,
  routes: Route[],
  middleware?: Middleware
) {
  // check if layout is exists in layouts list
  let layout = layoutsList.find(
    (layout) => layout.LayoutComponent === LayoutComponent
  );

  if (!layout) {
    layout = {
      LayoutComponent,
      routes: [],
      routesPaths: [],
      middleware: null
    };

    layoutsList.push(layout);
  }

  const appLocaleCodes = getLocaleCodes(),
    currentBaseAppPath = getCurrentBaseAppPath();

  routes = routes.map((route) => {
    route.originalPath = route.path;

    route.path = concatPath(currentBaseAppPath, route.path);

    (layout as Layout).routesPaths.push(route.path);

    return route;
  });

  // parse Locale routes
  if (appLocaleCodes) {
    routes.map((route) => {
      const routesWithLocaleCode = appLocaleCodes.map((appLocaleCode) => {
        const localePath = concatPath(appLocaleCode, route.path);

        return { ...route, path: localePath };
      });

      routes.push(...routesWithLocaleCode);

      return null;
    });
  }

  if(middleware) {
    layout.middleware = middleware
  }

  layout.routes = layout.routes.concat(routes);
}

/**
 * grouped the list of routes
 *
 * @param {RouterGroupOptions} routerGroupOptions
 * @param parentMiddleware
 * @returns {void}
 */
export function group(routerGroupOptions: RouterGroupOptions, parentMiddleware?: Middleware): void {
  const {
    path,
    layout = FragmentElement,
    routes = [],
    middleware,
  } = routerGroupOptions;

  partOfLayout(
    layout,
    routes.map((route) => {
      if (middleware) route.middleware = middleware;

      if (path) route.path = concatPath(path, route.path);

      return route;
    }), parentMiddleware
  );
}

/**
 * get the list of all routes in the application
 *
 * @returns {Route[]}
 */
export function routesList(): Route[] {
  return layoutsList.reduce((routes, layout) => {
    return routes.concat(
      layout.routes.map((route) => {
        route.layout = layout.LayoutComponent;

        return route;
      })
    );
  }, [] as Route[]);
}

/**
 * check if route is exist in application by the given segment
 *
 * @param {string} segment
 * @returns {boolean}
 */
export function isRouteExists(segment: string) {
  return Boolean(routesList().find((route) => route.path.match(segment)));
}
